/**
 * v0.3 一次性 db migration：把同 normalizedName 的多個 active items 合併成一個。
 *
 * Background：v0.2.x 的 quickAdd / markReceived 對 status='all_consumed' 的同名 item
 * 走「建新 item」分支，導致重複加同名後產生兩個 active items（雞蛋 row × 2）。
 * v0.3 已修 reuse 條件（status !== 'discarded'）— 但既有 db 已殘留 duplicate。
 *
 * 此 migration 在 init 啟動時跑一次：
 *   1. 掃 active items 按 normalizedName group
 *   2. 群組內 items > 1 → primary = 加入時間最早的（按 batch[0].addedAt 排序），
 *      其他 redundant 的 batches concat 到 primary、redundant 標 discarded
 *   3. **直接 patch db.items**（admin / repair 操作；不走 event log，
 *      因為 event-sourced「搬 batch」需要新 event type，scope 過大）
 *
 * 副作用：event log 裡這些 batches 仍 attached 到原 item — 若日後做
 * `rebuildProjections()` 會重新分散。鑒於 v0.1.x demo 階段沒有「rebuild
 * from event log」UI 入口，此風險可接受。production 上線前要把此 migration
 * 升級成走 event log 路徑（新 event：item_merged 或 batch_moved）。
 */
import { db } from '../db/db.js';
import type { Item } from '../db/schema.js';

export interface MergeReport {
  groupsFound: number;
  itemsMerged: number;
  batchesMoved: number;
}

export async function mergeDuplicateActiveItems(): Promise<MergeReport> {
  const items = await db.items.where('status').equals('active').toArray();
  const byKey = new Map<string, Item[]>();
  for (const it of items) {
    const arr = byKey.get(it.normalizedName) ?? [];
    arr.push(it);
    byKey.set(it.normalizedName, arr);
  }

  let groupsFound = 0;
  let itemsMerged = 0;
  let batchesMoved = 0;

  for (const group of byKey.values()) {
    if (group.length <= 1) continue;
    groupsFound += 1;
    // primary = 最早加入的（用第一個 batch 的 addedAt 當錨；沒 batch 的排到最後）
    const sorted = [...group].sort((a, b) => {
      const aTime = a.batches[0]?.addedAt ?? Number.MAX_SAFE_INTEGER;
      const bTime = b.batches[0]?.addedAt ?? Number.MAX_SAFE_INTEGER;
      return aTime - bTime;
    });
    const primary = sorted[0];
    if (!primary) continue;
    const redundants = sorted.slice(1);

    // concat batches 到 primary
    for (const r of redundants) {
      primary.batches.push(...r.batches);
      batchesMoved += r.batches.length;
      // primary 接管 minQuantity（若 redundant 有設且 primary 沒設）
      if (r.minQuantity !== undefined && primary.minQuantity === undefined) {
        primary.minQuantity = r.minQuantity;
      }
    }

    await db.transaction('rw', db.items, async () => {
      await db.items.put(primary);
      for (const r of redundants) {
        r.status = 'discarded';
        r.batches = []; // 已搬走，避免 rebuildProjections 找回
        await db.items.put(r);
      }
    });

    itemsMerged += redundants.length;
  }

  if (groupsFound > 0) {
    console.warn(
      `[migrations] mergeDuplicateActiveItems: ${groupsFound} group(s), ${itemsMerged} item(s) merged, ${batchesMoved} batch(es) moved`,
    );
  }
  return { groupsFound, itemsMerged, batchesMoved };
}
